import React, { useState, useEffect, useRef } from "react"
import {
  FiMousePointer,
  FiType,
  FiEdit2,
  FiPenTool,
  FiAward,
  FiCheckCircle,
  FiXCircle,
  FiLock,
  FiFileText,
  FiCheckSquare,
  FiChevronDown,
  FiRotateCcw,
  FiRotateCw,
  FiCopy,
  FiTrash2,
  FiMinus,
  FiSquare,
  FiCircle,
  FiAperture,
  FiDownload,
  FiUserCheck
} from "react-icons/fi"
import { MdHighlightAlt } from "react-icons/md"

import styles from "./PdfToolbar.module.css"
import ToolOptionsPanel from "./ToolOptionsPanel"

// Shape tools grouped together (geometric shapes only)
const shapeTools = [
  {
    id: "line", label: "Line", icon: FiMinus, shortcut: "L"
  },
  {
    id: "rectangle", label: "Rectangle", icon: FiSquare, shortcut: "R"
  },
  {
    id: "circle", label: "Circle", icon: FiCircle, shortcut: "C"
  },
  {
    id: "ellipse", label: "Oval", icon: FiAperture, shortcut: "O"
  }
]

// IDs of all shape tools for checking if current tool is a shape
const shapeToolIds = shapeTools.map(t => t.id)

export default function AnnotationToolbar({
  currentTool,
  onToolChange,
  canAnnotate = false,
  onSave,
  saving = false,
  selectedStampType = "approved",
  onStampTypeChange,
  onUndo = null,
  onRedo = null,
  canUndo = false,
  canRedo = false,
  onDelete = null,
  onDuplicate = null,
  canDelete = false,
  canDuplicate = false,
  toolSettings = {},
  onToolSettingsChange,
  signatureToolEnabled = false,
  // New props for export functionality
  onExport = null,
  exporting = false,
  canExport = false,
  // Props for Export PDF and Flag functionality
  onExportAndFlag = null,
  flagExporting = false,
  canShowFlagAction = false
}) {
  const [showStampDropdown, setShowStampDropdown] = useState(false)
  const [showShapesDropdown, setShowShapesDropdown] = useState(false)
  const stampDropdownRef = useRef(null)
  const shapesDropdownRef = useRef(null)

  // Close stamp dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (stampDropdownRef.current && !stampDropdownRef.current.contains(event.target)) {
        setShowStampDropdown(false)
      }
    }

    if (showStampDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }

    return undefined
  }, [showStampDropdown])

  // Close shapes dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (shapesDropdownRef.current && !shapesDropdownRef.current.contains(event.target)) {
        setShowShapesDropdown(false)
      }
    }

    if (showShapesDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
      }
    }

    return undefined
  }, [showShapesDropdown])

  // Main tools (not including shapes which are in dropdown)
  const mainTools = [
    {
      id: "select", label: "Select", icon: FiMousePointer, shortcut: "Esc"
    },
    {
      id: "highlight", label: "Highlight", icon: MdHighlightAlt, shortcut: "H"
    },
    {
      id: "text", label: "Text", icon: FiType, shortcut: "T"
    },
    {
      id: "draw", label: "Draw", icon: FiEdit2, shortcut: "D"
    },
    {
      id: "signatureImage",
      label: "Signature",
      icon: FiPenTool,
      shortcut: "G",
      disabled: !signatureToolEnabled,
      disabledTooltip: signatureToolEnabled ? null : "Upload your signature in Account Settings to enable"
    },
    {
      id: "signatureBlock",
      label: "Signature Block",
      icon: FiUserCheck,
      shortcut: "B",
      tooltip: "Add signature block with name, title, and surveyor ID"
    }
  ]

  const stampTypes = [
    {
      id: "approved", label: "Approved", icon: FiCheckCircle, textClass: "text-success"
    },
    {
      id: "rejected", label: "Rejected", icon: FiXCircle, textClass: "text-danger"
    },
    {
      id: "confidential", label: "Confidential", icon: FiLock, textClass: "text-danger"
    },
    {
      id: "draft", label: "Draft", icon: FiFileText, textClass: "text-warning"
    },
    {
      id: "final", label: "Final", icon: FiCheckSquare, textClass: "text-primary"
    }
  ]

  const currentStamp = stampTypes.find(s => s.id === selectedStampType) || stampTypes[0]
  
  // Check if current tool is a shape tool
  const isShapeToolActive = shapeToolIds.includes(currentTool)
  const currentShape = shapeTools.find(s => s.id === currentTool)
  const ShapeIcon = currentShape?.icon || FiSquare

  if (!canAnnotate) {
    return (
      <div className={styles.viewOnlyBanner}>
        <span>View only - no annotation permissions</span>
      </div>
    )
  }

  return (
    <>
      <div className={styles.toolbar} role="toolbar" aria-label="PDF annotation tools">
        <div className={styles.tools}>
          {/* Main tools */}
          {mainTools.map(tool => (
            <button
              key={tool.id}
              type="button"
              className={`${styles.toolButton} ${
                currentTool === tool.id ? styles.toolButtonActive : ""
              }`}
              onClick={() => !tool.disabled && onToolChange(tool.id)}
              disabled={tool.disabled}
              title={tool.disabled && tool.disabledTooltip
                ? tool.disabledTooltip
                : `${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ""}`}
              aria-pressed={currentTool === tool.id}>
              <tool.icon className={styles.toolIcon} aria-hidden="true" />
              <span className={styles.toolLabel}>{tool.label}</span>
            </button>
          ))}

          {/* Shapes dropdown */}
          <div className={styles.shapesDropdown} ref={shapesDropdownRef}>
            <button
              type="button"
              className={`${styles.toolButton} ${
                isShapeToolActive ? styles.toolButtonActive : ""
              }`}
              onClick={() => setShowShapesDropdown(!showShapesDropdown)}
              aria-expanded={showShapesDropdown}
              title={currentShape ? `Shapes: ${currentShape.label}` : "Shapes"}>
              <ShapeIcon className={styles.toolIcon} aria-hidden="true" />
              <span className={styles.toolLabel}>
                {currentShape?.label || "Shapes"}
              </span>
              <FiChevronDown className={styles.caretIcon} aria-hidden="true" />
            </button>
            {showShapesDropdown && (
              <ul className={styles.shapesMenu} role="menu">
                {shapeTools.map(shape => (
                  <li key={shape.id}>
                    <button
                      type="button"
                      className={`${styles.shapeMenuItem} ${
                        currentTool === shape.id ? styles.shapeMenuItemActive : ""
                      }`}
                      onClick={() => {
                        onToolChange(shape.id)
                        setShowShapesDropdown(false)
                      }}
                      role="menuitem">
                      <shape.icon className={styles.toolIcon} aria-hidden="true" />
                      <span>{shape.label}</span>
                      <span className={styles.shortcutHint}>{shape.shortcut}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Stamp dropdown */}
          <div className={styles.stampDropdown} ref={stampDropdownRef}>
            <button
              type="button"
              className={`${styles.toolButton} ${
                currentTool === "stamp" ? styles.toolButtonActive : ""
              }`}
              onClick={() => setShowStampDropdown(!showStampDropdown)}
              aria-expanded={showStampDropdown}
              title={`Stamp: ${currentStamp.label}`}>
              <FiAward className={styles.toolIcon} aria-hidden="true" />
              <span className={styles.toolLabel}>{currentStamp.label}</span>
              <FiChevronDown className={styles.caretIcon} aria-hidden="true" />
            </button>
            {showStampDropdown && (
              <ul className={styles.stampMenu} role="menu">
                {stampTypes.map(stamp => (
                  <li key={stamp.id}>
                    <button
                      type="button"
                      className={`${styles.stampMenuItem} ${
                        selectedStampType === stamp.id ? styles.stampMenuItemActive : ""
                      }`}
                      onClick={() => {
                        onStampTypeChange(stamp.id)
                        setShowStampDropdown(false)
                        onToolChange("stamp")
                      }}
                      role="menuitem">
                      <stamp.icon className={`${styles.toolIcon} ${stamp.textClass}`} aria-hidden="true" />
                      <span>{stamp.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          {/* Undo/Redo/Delete/Duplicate group */}
          <div className={styles.actionGroup}>
            <button
              type="button"
              className={`btn btn-sm btn-outline-secondary ${styles.actionButton}`}
              onClick={onUndo || undefined}
              disabled={!canUndo || !onUndo}
              title="Undo (Ctrl+Z)">
              <FiRotateCcw className={styles.actionIcon} aria-hidden="true" />
              <span className={styles.actionLabel}>Undo</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm btn-outline-secondary ${styles.actionButton}`}
              onClick={onRedo || undefined}
              disabled={!canRedo || !onRedo}
              title="Redo (Ctrl+Y)">
              <FiRotateCw className={styles.actionIcon} aria-hidden="true" />
              <span className={styles.actionLabel}>Redo</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm btn-outline-secondary ${styles.actionButton}`}
              onClick={onDuplicate || undefined}
              disabled={!canDuplicate || !onDuplicate}
              title="Duplicate (Ctrl+D)">
              <FiCopy className={styles.actionIcon} aria-hidden="true" />
              <span className={styles.actionLabel}>Duplicate</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm btn-outline-danger ${styles.actionButton}`}
              onClick={onDelete || undefined}
              disabled={!canDelete || !onDelete}
              title="Delete (Delete)">
              <FiTrash2 className={styles.actionIcon} aria-hidden="true" />
              <span className={styles.actionLabel}>Delete</span>
            </button>
          </div>

          {/* Save and Export buttons */}
          <div className={styles.saveExportGroup}>
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={onSave}
              disabled={saving}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-save" />
                  {" "}
                  Save Annotations
                </>
              )}
            </button>

            {canExport && onExport && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={onExport}
                disabled={exporting}
                title="Export PDF with annotations burned in">
                {exporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <FiDownload className={styles.actionIcon} aria-hidden="true" />
                    {" "}
                    Export PDF
                  </>
                )}
              </button>
            )}

            {canShowFlagAction && onExportAndFlag && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={onExportAndFlag}
                disabled={flagExporting || exporting}
                title="Export PDF and create a flag">
                {flagExporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-flag" aria-hidden="true" />
                    {" "}
                    Export PDF and Flag
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <ToolOptionsPanel
        activeTool={currentTool}
        settings={toolSettings}
        onSettingChange={onToolSettingsChange} />
    </>
  )
}
